import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Form, Spin, Switch, DatePicker } from "antd";
import {
  CheckOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  ProfileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

import {
  selectUserEditErrors,
  selectUserFromUserState,
} from "../../../redux-toolkit/user/user-selector";
import FormInput from "../../../components/FormInput/FormInput";
import IconButton from "../../../components/IconButton/IconButton";
import { updateUserInfo } from "../../../redux-toolkit/user/user-thunks";
import { resetInputForm } from "../../../redux-toolkit/user/user-slice";
import { BASE_URL } from "../../../constants/urlConstants";
import { Modal, message } from "antd";

import "./PersonalData.css";

interface PersonalDataFields {
  firstName: string;
  lastName: string;
  city: string;
  address: string;
  phoneNumber: string;
  postIndex: string;
}

const VIEW_FIELDS: { key: string; label: string }[] = [
  { key: "email",         label: "Email" },
  { key: "firstName",     label: "First Name" },
  { key: "lastName",      label: "Last Name" },
  { key: "city",          label: "City" },
  { key: "address",       label: "Address" },
  { key: "phoneNumber",   label: "Phone Number" },
  { key: "postIndex",     label: "Post Index" },
  { key: "roles",         label: "Role" },
  { key: "provider",      label: "Provider" },
  { key: "active",        label: "Status" },
  { key: "distributorId", label: "Distributor ID" },
  { key: "distributorMobile", label: "Distributor Number" },
  { key: "meEmpId",       label: "ME EmpId" },
];

// ── Distributor self-view fields (separate table, separate shape) ──
const DIST_VIEW_FIELDS: { key: string; label: string }[] = [
  { key: "businessName",   label: "Business Name" },
  { key: "email",          label: "Email" },
  { key: "mobile",         label: "Mobile" },
  { key: "address",        label: "Address" },
  { key: "pincode",        label: "Pincode" },
  { key: "upiId",          label: "UPI ID" },
  { key: "parentType",     label: "Parent Type" },
  { key: "meEmpId",        label: "ME EmpId" },
  { key: "serviceStatus",  label: "Service Status" },
  { key: "todayShopStatus", label: "Shop Status" },
];

/** Returns a safe display string. Reads distId from localStorage for distributorId field. */
const na = (val: any, key?: string): string => {
  if (val === null || val === undefined || String(val).trim() === "") return "—";
  if (key === "active") return val === true || val === "true" ? "Active" : "Inactive";
  if (key === "serviceStatus") return val === true || val === "true" ? "Active" : "Inactive";
  if (key === "todayShopStatus") return val === true || val === "true" ? "Open" : "Closed";
  if (Array.isArray(val)) return val.join(", ");
  return String(val);
};

/** Reads distId from localStorage, guarding against "null" / "undefined" strings. */
const getStoredDistId = (): string | null => {
  const val = localStorage.getItem("distId");
  return val && val !== "null" && val !== "undefined" ? val : null;
};

const PersonalData: FC = (): ReactElement => {
  const dispatch  = useDispatch();
  const [form]    = Form.useForm();
  const [distributorForm] = Form.useForm();
  const params    = useParams<{ id?: string }>();

  const loggedInUser = useSelector(selectUserFromUserState);
  const errors       = useSelector(selectUserEditErrors);
  const [editing, setEditing]   = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading]   = useState(false);
  const [distributorMobile, setDistributorMobile] = useState<string | null>(null);

  // ── Distributor self-service state ──
  const [isDistributor, setIsDistributor]   = useState<boolean>(false);
  const [distributorData, setDistributorData] = useState<any>(null);
  const [distLoading, setDistLoading]       = useState(false);

  const { firstNameError, lastNameError } = errors;

  const urlId          = params.id ? Number(params.id) : null;
  const isViewingOther = urlId !== null && urlId !== loggedInUser?.id;
  const activeUser     = isViewingOther ? userData : loggedInUser;
  const handleDeleteAccount = () => {
    Modal.confirm({
      title: "Delete your account?",
      content:
        "This will permanently delete your account and cannot be undone. Are you sure you want to continue?",
      okText: "Delete Account",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(`${BASE_URL}/api/v1/users/me`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            },
          });
          localStorage.clear();
          window.location.href = "/login";
        } catch (err) {
          console.error(err);
          message.error("Something went wrong while deleting your account. Please try again.");
        }
      },
    });
  };

  useEffect(() => {
    setIsDistributor(localStorage.getItem("role") === "3");
  }, []);

  useEffect(() => {
    dispatch(resetInputForm());

    // Distributor self-view/edit does not use the User form/state at all —
    // handled entirely by the distributor-specific effect below.
    if (isDistributor && !isViewingOther) {
      return;
    }

    if (isViewingOther && urlId) {
      setLoading(true);
      axios
        .get(`${BASE_URL}/api/v1/admin/user/${urlId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
          },
        })
        .then((res) => {
          setUserData(res.data);
          form.setFieldsValue({
            ...res.data,
            distributorId: getStoredDistId() ?? res.data.distributorId,
          });
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else if (loggedInUser) {
      const storedDistId = getStoredDistId();
      const storedDistName = localStorage.getItem("distName");
      const displayDistributor = storedDistId && storedDistName
        ? `${storedDistId}  -  ${storedDistName}`
        : storedDistId ?? (loggedInUser as any).distributorId;

      form.setFieldsValue({
        ...loggedInUser,
        distributorId: displayDistributor,
      });
    }
  }, [dispatch, urlId, loggedInUser, form, isDistributor, isViewingOther]);

  // ── Fetch distributor's mobile number using the stored distId (business code) ──
  // Unchanged — this powers the "Distributor Number" row for Admin/User views only.
  useEffect(() => {
    if (isDistributor) return; // distributor's own view shows mobile directly from their record
    const distId = getStoredDistId();
    if (!distId) {
      setDistributorMobile(null);
      return;
    }

    let cancelled = false;

    const fetchDistributorMobile = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/finalle/dist/code/${distId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setDistributorMobile(data.mobile || null);
      } catch {
        if (!cancelled) setDistributorMobile(null);
      }
    };

    fetchDistributorMobile();
    return () => { cancelled = true; };
  }, [loggedInUser, urlId, isDistributor]);

  // ── Fetch the distributor's own full record (self view/edit only) ──
  useEffect(() => {
    if (!isDistributor || isViewingOther) return;
    const distId = getStoredDistId();
    if (!distId) return;

    let cancelled = false;
    setDistLoading(true);

    fetch(`${BASE_URL}/api/finalle/dist/code/${distId}`)
      .then((res) => res.json())
      .then((rec) => {
        if (cancelled) return;
        setDistributorData(rec);
        distributorForm.setFieldsValue({
          ...rec,
          shopCloseUntil: rec.shopCloseUntil ? dayjs(rec.shopCloseUntil) : null,
        });
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setDistLoading(false); });

    return () => { cancelled = true; };
  }, [isDistributor, isViewingOther, distributorForm]);

  const onFormSubmit = (data: PersonalDataFields) => {
    const id = isViewingOther ? urlId : loggedInUser?.id;
    dispatch(updateUserInfo({ id, ...data }));
    setEditing(false);
  };

const onDistributorSubmit = async (values: any) => {
  try {
    const fd = new FormData();
    fd.append("businessName", values.businessName ?? "");
    fd.append("address", values.address ?? "");
    fd.append("mobile", values.mobile ?? "");
    fd.append("email", values.email ?? "");
    fd.append("todayShopStatus", String(values.todayShopStatus ?? true));
    if (values.todayShopStatus === false) {
      fd.append("shopCloseReason", values.shopCloseReason ?? "");
      fd.append(
        "shopCloseUntil",
        values.shopCloseUntil ? dayjs(values.shopCloseUntil).format("YYYY-MM-DDT23:59:59") : ""
      );
    }

    fd.append("upiId", distributorData?.upiId ?? "");
    fd.append("pincode", distributorData?.pincode ?? "");
    fd.append("parentType", distributorData?.parentType ?? "");
    if (distributorData?.meEmpId) fd.append("meEmpId", distributorData.meEmpId);
    fd.append("serviceStatus", String(distributorData?.serviceStatus ?? true));

    const res = await axios.post(
      `${BASE_URL}/api/finalle/updateDistributor/${distributorData.id}`,
      fd,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
      }
    );

    // ✅ refresh local state from the server's response instead of the stale fetch
    const updated = res.data;
    setDistributorData(updated);
    distributorForm.setFieldsValue({
      ...updated,
      shopCloseUntil: updated.shopCloseUntil ? dayjs(updated.shopCloseUntil) : null,
    });

    setEditing(false);
  } catch (err) {
    console.error(err);
  }
};

  const getInitial = () => {
    if (isDistributor && !isViewingOther) {
      const name = distributorData?.businessName || distributorData?.email || "D";
      return name.charAt(0).toUpperCase();
    }
    const name = activeUser?.firstName || activeUser?.email || "U";
    return name.charAt(0).toUpperCase();
  };

  const showLoading = isDistributor && !isViewingOther ? distLoading : loading;

  if (showLoading) {
    return (
      <div className="pd-page">
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="pd-page">

      {/* HEADER */}
      <div className="pd-header">
        <ProfileOutlined className="pd-header-icon" />
        <span className="pd-header-title">
          {isViewingOther ? "User Profile" : isDistributor ? "My Business" : "My Account"}
        </span>
        <button
          className={`pd-edit-btn ${editing ? "active" : ""}`}
          onClick={() => setEditing((p) => !p)}
        >
          {editing ? <><EyeInvisibleOutlined /> Cancel</> : <><EditOutlined /> Edit</>}
        </button>
      </div>

      <div className="pd-content">

        {/* PROFILE CARD */}
        <div className="pd-profile-card">
          <div className="pd-avatar">{getInitial()}</div>
          <div className="pd-profile-info">
            <div className="pd-profile-name">
              {isDistributor && !isViewingOther
                ? distributorData?.businessName || "—"
                : [activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="pd-profile-email">
              {isDistributor && !isViewingOther ? distributorData?.email || "—" : activeUser?.email || "—"}
            </div>
          </div>
          {isViewingOther && (
            <span className="pd-viewing-badge">Admin View</span>
          )}
        </div>

        {/* DATA SECTION */}
        {!editing ? (
          <div className="pd-data-card">
            <div className="pd-section-title">
              <UserOutlined /> {isDistributor && !isViewingOther ? "Business Details" : "Personal Details"}
            </div>
            <div className="pd-fields">
              {isDistributor && !isViewingOther ? (
                DIST_VIEW_FIELDS.map(({ key, label }) => {
                  const val = distributorData?.[key];
                  if (val === null || val === undefined) return null;
                  return (
                    <div key={key} className="pd-field-row">
                      <span className="pd-field-label">{label}</span>
                      <span className="pd-field-value">{na(val, key)}</span>
                    </div>
                  );
                })
              ) : (
                VIEW_FIELDS.map(({ key, label }) => {
                  const val = (activeUser as any)?.[key];

                  if (
                    (val === null || val === undefined) &&
                    key !== "distributorId" &&
                    key !== "distributorMobile" &&
                    key !== "adminId"
                  )
                    return null;

                  // Distributor Number row only makes sense if we actually have a distId at all
                  if (key === "distributorMobile" && !getStoredDistId()) return null;

                  return (
                    <div key={key} className="pd-field-row">
                      <span className="pd-field-label">{label}</span>
                      <span
                        className={`pd-field-value ${
                          key === "active"
                            ? val ? "pd-active" : "pd-inactive"
                            : ""
                        }`}
                      >
                        {key === "distributorId"
                          ? (() => {
                              const id = getStoredDistId();
                              const name = localStorage.getItem("distName");
                              return id && name ? `${id}  -  ${name}` : id || na(val, key);
                            })()
                          : key === "distributorMobile"
                          ? na(distributorMobile)
                          : na(val, key)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="pd-data-card">
            <div className="pd-section-title">
              <EditOutlined /> Edit Details
            </div>

            {isDistributor && !isViewingOther ? (
              <Form form={distributorForm} layout="vertical" onFinish={onDistributorSubmit} className="pd-form">
                <FormInput title="Business name" titleSpan={6} wrapperSpan={18} name="businessName" placeholder="Business name" />
                <FormInput title="Address"       titleSpan={6} wrapperSpan={18} name="address"      placeholder="Address" />
                <FormInput title="Mobile"        titleSpan={6} wrapperSpan={18} name="mobile"       placeholder="Mobile" />
                <FormInput title="Email"         titleSpan={6} wrapperSpan={18} name="email"        placeholder="Email" />

                {/* UPI ID — read-only, payment routing, admin-only change */}
                <div className="pd-field-row">
                  <span className="pd-field-label">UPI ID</span>
                  <span className="pd-field-value">{na(distributorData?.upiId)}</span>
                </div>

                <Form.Item name="todayShopStatus" label="Shop Open Today" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => getFieldValue("todayShopStatus") === false ? (
                    <>
                      <FormInput title="Close reason" titleSpan={6} wrapperSpan={18} name="shopCloseReason" placeholder="Reason" />
                      <Form.Item name="shopCloseUntil" label="Closed until">
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </>
                  ) : null}
                </Form.Item>

                <div className="pd-save-row">
                  <IconButton title="Save Changes" icon={<CheckOutlined />} />
                </div>
              </Form>
            ) : (
              <Form form={form} layout="vertical" onFinish={onFormSubmit} className="pd-form">
                <FormInput title="First name"    titleSpan={6} wrapperSpan={18} name="firstName"   error={firstNameError} placeholder="First name" />
                <FormInput title="Last name"     titleSpan={6} wrapperSpan={18} name="lastName"    error={lastNameError}  placeholder="Last name" />
                <FormInput title="City"          titleSpan={6} wrapperSpan={18} name="city"        placeholder="City" />
                <FormInput title="Address"       titleSpan={6} wrapperSpan={18} name="address"     placeholder="Address" />
                <FormInput title="Phone number"  titleSpan={6} wrapperSpan={18} name="phoneNumber" placeholder="Phone number" />
                <FormInput title="Post index"    titleSpan={6} wrapperSpan={18} name="postIndex"   placeholder="Post index" />
                <div className="pd-save-row">
                  <IconButton title="Save Changes" icon={<CheckOutlined />} />
                </div>
              </Form>
            )}
          </div>
        )}
        {!isViewingOther && !isDistributor && (
          <div style={{ textAlign: "center", marginTop: 32, marginBottom: 8 }}>
            <span
              onClick={handleDeleteAccount}
              style={{
                color: "#1677ff",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: 14,
                textDecoration: "underline",
              }}
            >
              Delete Account
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalData;